import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, AlertTriangle, Loader2 } from 'lucide-react';
import { useAuth } from '../../../src/contexts/AuthContext';
import { useToast } from '../../../src/contexts/ToastContext';
import { reportarFalha } from './protocoloActions';

interface ReportarFalhaModalProps {
  rateioId: string;
  onClose: () => void;
  onReportado: () => void;
}

const ReportarFalhaModal: React.FC<ReportarFalhaModalProps> = ({ rateioId, onClose, onReportado }) => {
  const { profile } = useAuth();
  const { addToast } = useToast();
  const [motivo, setMotivo] = useState('');
  const [saving, setSaving] = useState(false);

  const handleConfirm = async () => {
    if (!motivo.trim()) {
      addToast('Informe o motivo da falha.', 'error');
      return;
    }
    setSaving(true);
    try {
      await reportarFalha(rateioId, motivo.trim(), profile?.full_name);
      addToast('Falha reportada — rateio voltou para Aguardando Protocolo.', 'success');
      onReportado();
    } catch (error) {
      console.error('Error reporting falha', error);
      addToast('Erro ao reportar falha.', 'error');
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-gray-700">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white font-poppins flex items-center gap-2">
            <AlertTriangle size={20} className="text-amber-500" /> Reportar Falha
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-3">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            O rateio volta para "Aguardando Protocolo" e o Gestor é notificado. Descreva o que falhou
            (ex: e-mail retornou/bounce, distribuidora rejeitou a planilha).
          </p>
          <textarea
            value={motivo}
            onChange={e => setMotivo(e.target.value)}
            rows={4}
            placeholder="Motivo da falha..."
            className="w-full bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2"
          />
        </div>

        <div className="flex justify-end gap-3 p-6 border-t border-gray-100 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            Cancelar
          </button>
          <button
            onClick={handleConfirm}
            disabled={saving}
            className="px-4 py-2 rounded-lg bg-amber-600 text-white font-medium hover:bg-amber-700 disabled:opacity-60 flex items-center gap-2"
          >
            {saving && <Loader2 size={16} className="animate-spin" />}
            Confirmar Falha
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ReportarFalhaModal;
